import React, { useEffect, useState, useRef } from "react";
import "./App.css";
import "./ui-kit.css";
import { ReactTagManager } from "react-gtm-ts";
import debounce from 'lodash/debounce';

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

// Add type definitions at the top
interface SocialIcon {
  title: string;
  cls: string;
  icon: string;
  url: string;
}

interface SocialSettings {
  settings: {
    position: string;
    shape: string;
    style: string;
    theme: string;
    type: string;
    sidebar: string;
  };
  hideArrow: boolean;
  addSpace: boolean;
  mobileOff: boolean;
  mobilePosBottom: boolean;
  icons: SocialIcon[];
}

function App() {
  ReactTagManager.init(tagManagerArgs);
  const intercomInitialized = useRef(false);
  const BASE_URL = "https://wix.certifiedcode.io/ecwid-social-share";
  const [instance, setInstance] = React.useState({} as any);
  const [isEnabled, setIsEnabled] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const defaultOptions: SocialSettings = {
    settings: {
      position: "left",
      shape: "square",
      style: "grow",
      theme: "color",
      type: "follow",
      sidebar: "fixed"
    },
    hideArrow: true,
    addSpace: false,
    mobileOff: false,
    mobilePosBottom: true,
    icons: [
      {
        title: "Twitter",
        cls: "sb-twitter",
        icon: "fab fa-twitter selected",
        url: ""
      },
      {
        title: "YouTube",
        cls: "sb-youtube",
        icon: "fab fa-youtube selected",
        url: ""
      },
      {
        title: "TikTok",
        cls: "sb-tiktok",
        icon: "fab fa-tiktok selected",
        url: ""
      },
      {
        title: "Facebook",
        cls: "sb-facebook",
        icon: "fab fa-facebook selected",
        url: ""
      },
      {
        title: "Instagram",
        cls: "sb-instagram",
        icon: "fab fa-instagram selected",
        url: ""
      }
    ]
  };
  const [options, setOptions] = useState<SocialSettings>(defaultOptions);

  const showSaveNotification = () => {
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  const handleOptionChange = (key: string, value: string | boolean) => {
    const newOptions = { ...options };
    
    if (['position', 'shape', 'style', 'theme', 'type', 'sidebar'].includes(key)) {
      newOptions.settings[key as keyof typeof newOptions.settings] = value as string;
    } else if (['hideArrow', 'addSpace', 'mobileOff', 'mobilePosBottom'].includes(key)) {
      (newOptions as any)[key] = value as boolean;
    } else if (key.startsWith('icon_')) {
      const [_, index, field] = key.split('_');
      if (field === 'url') {
        // Trim the URL to remove whitespace
        const trimmedUrl = (value as string).trim();
        newOptions.icons[parseInt(index)].url = trimmedUrl;
      }
    }
    
    setOptions(newOptions);
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    // Create a copy for the server that only includes filled URLs
    const serverConfig = {
      ...options,
      icons: options.icons.filter(icon => icon.url.trim() !== '')
    };

    fetch(BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/settings", {
      method: "POST",
      headers: {
        Authorization: new URLSearchParams(window.location.search).get(
          "payload"
        )!,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        isEnabled,
        config: serverConfig
      }),
    })
      .then((response) => response.json())
      .then((data: { config: SocialSettings }) => {
        if (data.config) {
          // Merge the server response with our full list of icons
          const mergedConfig = {
            ...data.config,
            icons: defaultOptions.icons.map(defaultIcon => {
              const serverIcon = data.config.icons.find((icon: SocialIcon) => icon.title === defaultIcon.title);
              return serverIcon || { ...defaultIcon, url: '' };
            })
          };
          setOptions(mergedConfig);
          setHasUnsavedChanges(false);
          showSaveNotification();
        }
      })
      .catch((error) => {
        console.error('Error saving settings:', error);
      });
  };

  useEffect(() => {
    if (!intercomInitialized.current && instance.intercomToken && instance.storeProfile && instance.app) {
      const name = instance.storeProfile.account.accountName;
      const registrationDate = new Date(
        instance.storeProfile.account.registrationDate
      );
      const created_at = Math.floor(registrationDate.getTime() / 1000);
      const phone = instance.storeProfile.company.phone;
      const email = instance.storeProfile.account.accountEmail;
      const utm_source = instance.app.client_id;
      const options = {
        email,
        name,
        created_at,
        phone,
        user_hash: instance.intercomToken,
        utm_source,
      };
      window.Intercom("boot", options);
      intercomInitialized.current = true;
    }
  }, [instance]);

  useEffect(() => {
    try {
      // Initialize the application
      window.EcwidApp.init({
        app_id: "all-in-one-social-share-button",
        autoloadedflag: true,
        autoheight: false,
      });

      getInstance();
      
      // Function to update height
      const updateHeight = () => {
        var body = document.body,
          html = document.documentElement;

        var height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );

        window.EcwidApp.setSize({ height: height });
      };

      // Initial height update
      updateHeight();

      // Set up intervals for both instance and height updates
      const instanceIntervalId = setInterval(getInstance, 10000);
      const heightIntervalId = setInterval(updateHeight, 1000);

      // Add settings fetch after getInstance
      if (instance) {
        getSettings();
      }

      // Cleanup intervals on component unmount
      return () => {
        clearInterval(instanceIntervalId);
        clearInterval(heightIntervalId);
      };
    } catch (error) {
      console.error(error);
    }
  }, []);

  function getInstance() {
    fetch(
      BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/auth",
      {
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((payload) => {
        setInstance(payload);
        window.EcwidApp.ready();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  function getSettings() {
    fetch(BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/settings", {
      method: "GET",
      headers: {
        Authorization: new URLSearchParams(window.location.search).get(
          "payload"
        )!,
      },
    })
      .then((response) => response.json())
      .then((data: { config: SocialSettings; isEnabled: boolean }) => {
        console.log(data);
        setIsEnabled(data.isEnabled);
        if (data.config) {
          // Merge with default options and maintain all social media options
          const mergedConfig = {
            ...defaultOptions,
            ...data.config,
            settings: {
              ...defaultOptions.settings,
              ...(data.config.settings || {})
            },
            icons: defaultOptions.icons.map(defaultIcon => {
              const serverIcon = data.config.icons?.find((icon: SocialIcon) => icon.title === defaultIcon.title);
              return serverIcon || { ...defaultIcon, url: '' };
            })
          };
          setOptions(mergedConfig);
        }
      })
      .catch(() => {
        setOptions(defaultOptions);
      });
  }

  const handleToggle = () => {
    if (!instance?.subscription) {
      // Open upgrade page if not subscribed
      window.open(
        instance?.storeProfile
          ? encodeURI(
              "https://www.certifiedcode.us/upgrade/apps?checkout=" +
                JSON.stringify({
                  settings: {
                    allowLogout: false,
                    allowDiscountRemoval: false,
                  },
                  items: [{ priceId: "pri_01jmexg633p8ff2nqhkpydh07f", quantity: 1 }],
                  customer: {
                    email: instance.storeProfile.account.accountEmail,
                    business: { name: instance.storeProfile.account.accountName },
                    address: {
                      countryCode: instance.storeProfile.company.countryCode,
                      postalCode: instance.storeProfile.company.postalCode,
                      city: instance.storeProfile.company.city,
                    },
                  },
                  customData: { store_id: `${instance.store_id}` },
                })
            )
          : undefined,
        "_blank"
      );
    } else {
      const newEnabledState = !isEnabled;
      setIsEnabled(newEnabledState);
      
      // Create a copy for the server that only includes filled URLs
      const serverConfig = {
        ...options,
        icons: options.icons.filter(icon => icon.url.trim() !== '')
      };

      fetch(BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/settings", {
        method: "POST",
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isEnabled: newEnabledState,
          config: serverConfig
        }),
      })
        .then((response) => response.json())
        .then((data: { config: SocialSettings; isEnabled: boolean }) => {
          setIsEnabled(data.isEnabled);
          if (data.config) {
            // Merge the server response with our full list of icons
            const mergedConfig = {
              ...data.config,
              icons: defaultOptions.icons.map(defaultIcon => {
                const serverIcon = data.config.icons.find((icon: SocialIcon) => icon.title === defaultIcon.title);
                return serverIcon || { ...defaultIcon, url: '' };
              })
            };
            setOptions(mergedConfig);
          }
        })
        .catch(() => {
          setIsEnabled(!newEnabledState);
        });
    }
  };

  return (
    <div className="container">
      <div className="heading-1">All In One Social Share Button</div>
      <div className="text-sm">Easily share page to WhatsApp, Facebook and more</div>

      <div className="card">
        <div className="card__header">
          <div className="heading-2">All In One Social Share Button overview</div>
          <div className="text-sm">Transform your browsing experience with premium smooth scrolling features. Plans start from $10.00/month.</div>
        </div>
        <div className="card__body">
          <div className="card" style={{ backgroundColor: '#2964de' }}>
            <div className="card__body">
              <div className="heading-2" style={{ color: 'white' }}>Welcome to All In One Social Share Button</div>
              <div className="text-sm" style={{ color: 'white', marginBottom: 'var(--spacing-lg)' }}>
                The ultimate social integration app that empowers you to customize and enhance your social media experience like never before.
              </div>
              {instance.store_id ? (
                <div>
                  <label className="checkbox">
                    <input 
                      type="checkbox"
                      className="checkbox__input"
                      checked={isEnabled}
                      onChange={handleToggle}
                    />
                    <div className="checkbox__box">
                      <div className="checkbox__mark"></div>
                    </div>
                  </label>
                </div>
              ) : (
                <div className="loader"></div>
              )}
              <div className="text-sm" style={{ color: 'white', marginTop: 'var(--spacing-md)' }}>
                {instance?.subscription ? 'Click to toggle social share buttons' : 'Upgrade to enable social share buttons'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <div className="heading-2">Customize Your Social Share Experience</div>
          <div className="text-sm">Personalize how your social share buttons appear and behave</div>
        </div>
        <div className="card__body">
          {!instance?.subscription && (
            <div className="alert alert--warning">
              Upgrade to apply these customization options to your store
            </div>
          )}
          
          <div className="form-group">
            <div className="form-group__header">
              <div className="form-group__title">Position</div>
              <div className="form-group__description">Choose where the social share buttons will appear on your page</div>
            </div>
            <div className="select-field">
              <select 
                className="select-field__control"
                value={options.settings.position}
                onChange={(e) => handleOptionChange('position', e.target.value)}
                disabled={!instance?.subscription}
              >
                <option value="left">Left</option>
                <option value="right">Right</option>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
              </select>
              <span className="select-field__arrow">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" focusable="false">
                  <path d="M7.85 10l5.02 4.9 5.27-4.9c.65-.66 1.71-.66 2.36 0 .65.67.65 1.74 0 2.4l-6.45 6.1c-.33.33-.76.5-1.18.5-.43 0-.86-.17-1.18-.5l-6.21-6.1c-.65-.66-.65-1.74 0-2.41.66-.65 1.72-.65 2.37.01z"></path>
                </svg>
              </span>
            </div>
          </div>

          <div className="form-group">
            <div className="form-group__header">
              <div className="form-group__title">Sidebar Type</div>
              <div className="form-group__description">Choose how the sidebar behaves when scrolling</div>
            </div>
            <div className="select-field">
              <select 
                className="select-field__control"
                value={options.settings.sidebar}
                onChange={(e) => handleOptionChange('sidebar', e.target.value)}
                disabled={!instance?.subscription}
              >
                <option value="fixed">Fixed</option>
                <option value="absolute">Absolute</option>
              </select>
              <span className="select-field__arrow">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" focusable="false">
                  <path d="M7.85 10l5.02 4.9 5.27-4.9c.65-.66 1.71-.66 2.36 0 .65.67.65 1.74 0 2.4l-6.45 6.1c-.33.33-.76.5-1.18.5-.43 0-.86-.17-1.18-.5l-6.21-6.1c-.65-.66-.65-1.74 0-2.41.66-.65 1.72-.65 2.37.01z"></path>
                </svg>
              </span>
            </div>
          </div>

          <div className="form-group">
            <div className="form-group__header">
              <div className="form-group__title">Shape</div>
              <div className="form-group__description">Select the shape style for your social share icons</div>
            </div>
            <div className="select-field">
              <select 
                className="select-field__control"
                value={options.settings.shape}
                onChange={(e) => handleOptionChange('shape', e.target.value)}
                disabled={!instance?.subscription}
              >
                <option value="square">Square</option>
                <option value="round">Round</option>
                <option value="circle">Circle</option>
              </select>
              <span className="select-field__arrow">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" focusable="false">
                  <path d="M7.85 10l5.02 4.9 5.27-4.9c.65-.66 1.71-.66 2.36 0 .65.67.65 1.74 0 2.4l-6.45 6.1c-.33.33-.76.5-1.18.5-.43 0-.86-.17-1.18-.5l-6.21-6.1c-.65-.66-.65-1.74 0-2.41.66-.65 1.72-.65 2.37.01z"></path>
                </svg>
              </span>
            </div>
          </div>

          <div className="form-group">
            <div className="form-group__header">
              <div className="form-group__title">Style</div>
              <div className="form-group__description">Choose the hover effect style</div>
            </div>
            <div className="select-field">
              <select 
                className="select-field__control"
                value={options.settings.style}
                onChange={(e) => handleOptionChange('style', e.target.value)}
                disabled={!instance?.subscription}
              >
                <option value="grow">Grow</option>
                <option value="simple">Simple</option>
              </select>
              <span className="select-field__arrow">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" focusable="false">
                  <path d="M7.85 10l5.02 4.9 5.27-4.9c.65-.66 1.71-.66 2.36 0 .65.67.65 1.74 0 2.4l-6.45 6.1c-.33.33-.76.5-1.18.5-.43 0-.86-.17-1.18-.5l-6.21-6.1c-.65-.66-.65-1.74 0-2.41.66-.65 1.72-.65 2.37.01z"></path>
                </svg>
              </span>
            </div>
          </div>

          <div className="form-group">
            <div className="form-group__header">
              <div className="form-group__title">Theme</div>
              <div className="form-group__description">Select a color theme that matches your store's design</div>
            </div>
            <div className="select-field">
              <select 
                className="select-field__control"
                value={options.settings.theme}
                onChange={(e) => handleOptionChange('theme', e.target.value)}
                disabled={!instance?.subscription}
              >
                <option value="color">Color</option>
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
              <span className="select-field__arrow">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" focusable="false">
                  <path d="M7.85 10l5.02 4.9 5.27-4.9c.65-.66 1.71-.66 2.36 0 .65.67.65 1.74 0 2.4l-6.45 6.1c-.33.33-.76.5-1.18.5-.43 0-.86-.17-1.18-.5l-6.21-6.1c-.65-.66-.65-1.74 0-2.41.66-.65 1.72-.65 2.37.01z"></path>
                </svg>
              </span>
            </div>
          </div>

          <div className="form-group">
            <div className="form-group__header">
              <div className="form-group__title">Additional Options</div>
            </div>
            <div className="checkbox-group">
              <label className="checkbox">
                <input
                  type="checkbox"
                  className="checkbox__input"
                  checked={options.hideArrow}
                  onChange={(e) => handleOptionChange('hideArrow', e.target.checked)}
                  disabled={!instance?.subscription}
                />
                <div className="checkbox__box">
                  <div className="checkbox__mark"></div>
                </div>
                <span className="checkbox__label">Hide Arrow</span>
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  className="checkbox__input"
                  checked={options.addSpace}
                  onChange={(e) => handleOptionChange('addSpace', e.target.checked)}
                  disabled={!instance?.subscription}
                />
                <div className="checkbox__box">
                  <div className="checkbox__mark"></div>
                </div>
                <span className="checkbox__label">Add Space Between Icons</span>
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  className="checkbox__input"
                  checked={options.mobileOff}
                  onChange={(e) => handleOptionChange('mobileOff', e.target.checked)}
                  disabled={!instance?.subscription}
                />
                <div className="checkbox__box">
                  <div className="checkbox__mark"></div>
                </div>
                <span className="checkbox__label">Hide on Mobile</span>
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  className="checkbox__input"
                  checked={options.mobilePosBottom}
                  onChange={(e) => handleOptionChange('mobilePosBottom', e.target.checked)}
                  disabled={!instance?.subscription}
                />
                <div className="checkbox__box">
                  <div className="checkbox__mark"></div>
                </div>
                <span className="checkbox__label">Position Bottom on Mobile</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-group__header">
              <div className="form-group__title">Social Media URLs</div>
              <div className="form-group__description">Enter the URLs for your social media profiles. Only buttons with URLs will be displayed.</div>
            </div>
            {options.icons.map((icon, index) => (
              <div key={icon.title} className="input-field">
                <label className="input-field__label">{icon.title} URL</label>
                <input 
                  type="text" 
                  className="input-field__control" 
                  value={icon.url}
                  onChange={(e) => handleOptionChange(`icon_${index}_url`, e.target.value)}
                  disabled={!instance?.subscription}
                  placeholder={`Enter ${icon.title} URL`}
                />
                <div className={`input-field__status ${icon.url ? 'input-field__status--active' : 'input-field__status--inactive'}`}>
                  <div className="input-field__status-icon">
                    {icon.url ? (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    )}
                  </div>
                  <span>
                    {icon.url ? 'Button will be displayed' : 'Button will be hidden'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="button button--primary"
              onClick={instance?.subscription ? handleSave : handleToggle}
              disabled={instance?.subscription && !hasUnsavedChanges}
            >
              {instance?.subscription ? (hasUnsavedChanges ? 'Save Changes' : 'No Changes to Save') : 'Upgrade to Apply Changes'}
            </button>
          </div>
        </div>
      </div>

      {showNotification && (
        <div className="notification notification--success">
          <div className="notification__icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="notification__message">Changes saved successfully</div>
        </div>
      )}
    </div>
  );
}

export default App;
